import type { AdminOrderStatus } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Order status badge chip for the orders list + detail drawer (AUT-123).
 * Colours mirror the mockup's `badge--{success,warning,danger,info,brand,
 * neutral}` classes via MUI Chip `color`. Labels resolve through i18n
 * (`orders.status.<value>`; CLAUDE.md rule #11).
 *
 * Typed as a total `Record<AdminOrderStatus, …>` so a status-enum change
 * in `@carcheck/shared` fails typecheck here rather than rendering an
 * uncoloured chip at runtime.
 */
type ChipColor = ChipProps['color'];

const STATUS_COLOR: Record<AdminOrderStatus, ChipColor> = {
  draft: 'default',
  awaiting_payment: 'default',
  paid: 'info',
  accepted: 'primary',
  in_progress: 'info',
  report_submitted: 'warning',
  completed: 'success',
  released: 'success',
  disputed: 'error',
  cancelled: 'error',
  expired: 'default',
};

export const OrderStatusBadge = ({ status }: { status: AdminOrderStatus }): ReactElement => {
  const { t } = useTranslation();
  return <Chip size="small" color={STATUS_COLOR[status]} label={t(`orders.status.${status}`)} />;
};
